/**
 * `/tax-exemption`: a signed-in customer uploads a tax exemption certificate
 * (`components/TaxExemptionPage/index.tsx`). Behind `ProtectedRoute` with
 * `Permission.Tax_Exempt_Submit`: a signed-out visitor is sent to `/login` and back here
 * afterwards (sessionStorage `redirectTo`), and a user without the permission is sent away; see
 * `components/NextLayouts/ProtectedRoute.tsx`. Built for every brand. The page renders no
 * `MetaHead`, so it has no title, description or canonical link.
 *
 * The permission is a UI gate only: it decides who sees the form. The form posts to the Common
 * API's public contact-us route (`/v1/common/v2/public/contact-us`), which doesn't require the
 * sign-in token and doesn't check `Tax_Exempt_Submit`, so a direct call to that route can also
 * send a tax exemption request. That is intended: a submission is an email to the tax team.
 */
import React from "react";
import type { NextPageWithLayout } from "@pages/_app";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";
import { HeaderPageProps, allContentfulHeaderCalls } from "@ryerson/frontend.contentful";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import { TaxExemptionPage } from "@components/TaxExemptionPage";
import { ProtectedRoute } from "@components/NextLayouts/ProtectedRoute";
import { Permission } from "@ryerson/common.permissions";
/** Fetches only the Contentful header, locations and footer data that `HeaderLayout` needs. */
export const getStaticProps = async () => {
	return await allContentfulHeaderCalls();
};

/** The `/tax-exemption` page; see the file header. */
const Page: NextPageWithLayout<HeaderPageProps> = (props) => {
	return (
		<HeaderLayout {...getHeaderData(props)}>
			<ProtectedRoute
				component={TaxExemptionPage}
				permission={Permission.Tax_Exempt_Submit}
			/>
		</HeaderLayout>
	);
};

export default Page;
